/* Los cuatro destinos de abajo, en el teléfono.
 *
 * ── QUÉ RESUELVE ─────────────────────────────────────────────────────────
 *
 * Portería carga con la barrera abajo y un camión esperando; el taller, con
 * las manos sucias. En esa situación, abrir un cajón lateral y recorrer 25
 * entradas para llegar a «Ingreso y egreso» es la diferencia entre cargarlo y
 * no cargarlo — y lo que no se carga en el momento no se carga nunca.
 *
 * ── NO ES UNA APP APARTE NI UN SUBCONJUNTO DE PANTALLAS ──────────────────
 *
 * Todo el hub sigue estando: el cuarto botón es **Menú** y abre el rail
 * entero. Lo que cambia es qué queda a un dedo. Es la misma regla que el rail
 * plegable: acortar el camino, nunca esconder la puerta.
 *
 * ── LOS ATAJOS SALEN DEL PERMISO, NO DEL USO ─────────────────────────────
 *
 * El sector de una persona es **el universo donde más pantallas puede abrir**.
 * Sale de lo que alguien le dio a propósito, no de lo que tocó ayer: una barra
 * que se reacomoda según lo último que hiciste obliga a mirar antes de apretar,
 * y entonces deja de ser un atajo. Y con el empate manda el orden del catálogo,
 * que es estable — no el azar del `Object.keys`.
 *
 * **Novedades no compite**: Inicio ya tiene su lugar fijo, y si ese universo
 * ganara la barra serían Inicio, Tickets y Mensajes para todo el mundo.
 *
 * Es puro: entran los grupos ya filtrados por permiso, salen los destinos. Se
 * prueba en una tabla.
 */

/* Cuántos atajos, sin contar Inicio ni Menú. Cuatro columnas es lo que entra
 * legible en un teléfono angosto; con cinco, los rótulos se parten en dos
 * renglones y hay que leerlos en vez de reconocerlos. */
import { iconoDeUniverso } from "./Icono";

export const ATAJOS = 2;

/* Inicio va fijo y primero: es la pantalla que dice qué hay que hacer hoy, y
 * en el teléfono es también la forma de volver. No sale del cálculo porque no
 * depende del sector de nadie. */
export const INICIO = { id: "novedades", label: "Inicio", ruta: "/", icono: "casa" };

/* El universo de Inicio, que no compite por los atajos. */
const NO_COMPITE = "Novedades";

/* En qué sector trabaja esta persona.
 *
 * `null` cuando no hay ninguno con pantallas — alguien recién dado de alta, o
 * los permisos que todavía no llegaron. Ahí la barra muestra sólo Inicio y
 * Menú: inventar dos atajos sería mandar a pantallas que esa persona no puede
 * abrir.
 */
export function universoDominante(grupos = []) {
  const cuenta = new Map();
  const orden = [];
  for (const g of grupos) {
    if (!g.universo || g.universo === NO_COMPITE) continue;
    const abribles = (g.items || []).filter((i) => i.donde !== "pronto").length;
    if (!abribles) continue;
    if (!cuenta.has(g.universo)) orden.push(g.universo);
    cuenta.set(g.universo, (cuenta.get(g.universo) || 0) + abribles);
  }
  if (!orden.length) return null;
  /* El empate lo rompe el orden del catálogo: con `sort` sobre un Map, dos
   * universos con la misma cantidad podrían intercambiarse entre cargas y la
   * barra cambiaría sola sin que nadie tocara nada. */
  return orden.reduce((mejor, u) =>
    cuenta.get(u) > cuenta.get(mejor) ? u : mejor,
  );
}

/* Los destinos de la barra: Inicio, hasta dos del sector, y Menú.
 *
 * Menú NO está acá: lo dibuja el componente, porque no es una ruta sino la
 * acción de abrir el rail. Meterlo en esta lista obligaría a inventarle una
 * dirección que no existe.
 */
export function destinos(grupos = []) {
  const universo = universoDominante(grupos);
  if (!universo) return [INICIO];

  const atajos = grupos
    .filter((g) => g.universo === universo)
    .flatMap((g) => g.items || [])
    /* Las `pronto` no van: es una baldosa que no abre nada, y en una barra de
     * cuatro botones ocupar un lugar con algo que todavía no existe es peor
     * que dejar el lugar vacío. */
    .filter((i) => i.donde !== "pronto")
    .slice(0, ATAJOS)
    .map((i) => ({
      id: i.id,
      /* EL RÓTULO CORTO, y el largo de respaldo. «Ingreso y egreso» en una
       * columna de un cuarto de pantalla se parte en tres renglones o se
       * recorta, y un rótulo recortado hay que leerlo entero para saber qué
       * es. El catálogo trae `corto` sólo donde hace falta. */
      label: i.corto || i.label,
      ruta: i.donde === "flotabot" ? `/panel/${i.id}` : i.ruta,
      icono: iconoDeUniverso(universo),
    }));

  return [INICIO, ...atajos];
}
